// File: components/content/SectionViewer.js

import React, { useEffect, useState } from 'react';
import { loadSection, loadSectionTranslation } from '../../utils/dataLoader';
import { useLanguage } from '../../i18n/LanguageContext';
import { formatDate, calculateReadingTime, highlightText, escapeHtml } from '../../utils/helpers';
import { DATE_FORMATS, ICONS, FEATURES } from '../../utils/constants';

// ----------------------------------------------------------------------
// Regulation text formatting
//
// CFR paragraphs nest as (a) → (1) → (i) → (A), and each level indents a
// step deeper. The section text arrives flattened, so paragraphs are
// recovered with a sentence-boundary rule: a marker starts a paragraph at
// the start of the text, after terminal punctuation (optionally through a
// joining "and"/"or" — "...repaired; and (3) When..."), or after a
// heading dash ("Provision and use —(1) ..."). Markers anywhere else are
// inline cross-references ("paragraphs (a)(2) through (a)(7)",
// "29 CFR 1910.12(b)") and stay inside their sentence.
// ----------------------------------------------------------------------

// Hazard Communication sections — these require accessible SDS, so the
// viewer cross-links the companion SDS.guide product when displaying them.
const HAZCOM_SECTIONS = ['1910.1200', '1910.1201', '1926.59'];

const LETTER_SEQ = 'abcdefghijklmnopqrstuvwxyz';
const ROMAN_SUCCESSOR = { i: 'ii', v: 'vi', x: 'xi' };

const MARKER_BOUNDARY =
    /(^|[.;:!?”"]\s+(?:and\s+|or\s+)?|[—–]\s*)\((\d+|[ivxlc]{2,}|[a-z]|[A-Z])\)\s*/g;

/**
 * Map a marker to its indent level: 1 = (a), 2 = (1), 3 = (i), 4 = (A).
 *
 * Single (i)/(v)/(x) are ambiguous — the letter after (h)/(u)/(w) or a
 * roman numeral — so they're classified by what follows ((ii) means
 * roman, (j) means letter) and by the letter sequence seen so far.
 */
function classifyMarker(marker, nextMarker, lastLetter) {
    if (/^\d+$/.test(marker)) return 2;
    if (/^[A-Z]$/.test(marker)) return 4;
    if (marker.length > 1) return 3; // multi-character roman numeral

    if (!'ivxlc'.includes(marker)) return 1; // unambiguous letter

    if (nextMarker === ROMAN_SUCCESSOR[marker]) return 3;
    // Uppercase items are children of roman numerals, never of letters —
    // "(i) Employers must: (A) Select..." makes this (i) a roman
    if (nextMarker === 'A') return 3;
    if (nextMarker === LETTER_SEQ[LETTER_SEQ.indexOf(marker) + 1]) return 1;
    if (lastLetter && LETTER_SEQ[LETTER_SEQ.indexOf(lastLetter) + 1] === marker) return 1;

    // (l) and (c) are almost never roman numerals in regulation lists
    return 'lc'.includes(marker) ? 1 : 3;
}

// Arabic (and other RTL) bidi: wrap maximal runs of non-Arabic content that
// contain a Latin letter, digit, or § in <bdi dir="ltr"> so citations, numbers,
// units, and cross-references stay left-to-right and in order inside RTL text.
// Runs are Latin because we keep CFR designators/numbers/citations verbatim.
const ARABIC = '\\u0600-\\u06FF\\u0750-\\u077F\\u08A0-\\u08FF\\uFB50-\\uFDFF\\uFE70-\\uFEFF';
const LTR_RUN = new RegExp(`[^${ARABIC}]*[A-Za-z0-9§][^${ARABIC}]*`, 'g');
function isolateLtrRuns(escaped) {
    return escaped.replace(LTR_RUN, (m) => (m.trim() ? `<bdi dir="ltr">${m}</bdi>` : m));
}

function formatRegulationText(text, searchTerm = '', rtl = false) {
    // Locate every structural marker
    const markers = [];
    const re = new RegExp(MARKER_BOUNDARY.source, 'g');
    let match;
    while ((match = re.exec(text)) !== null) {
        markers.push({
            marker: match[2],
            bodyStart: match.index + match[0].length,
            boundaryEnd: match.index + match[1].length,
        });
    }

    // Slice into paragraphs. Text before the first marker is an unindented
    // intro; each marker owns the text up to the next marker's boundary
    // (the boundary punctuation stays with the preceding paragraph).
    const paragraphs = [];
    const introEnd = markers.length ? markers[0].boundaryEnd : text.length;
    const intro = text.slice(0, introEnd).trim();
    if (intro) {
        paragraphs.push({ marker: null, level: 0, body: intro });
    }

    let lastLetter = null;
    markers.forEach((m, idx) => {
        const next = markers[idx + 1];
        const body = text.slice(m.bodyStart, next ? next.boundaryEnd : text.length).trim();
        const level = classifyMarker(m.marker, next ? next.marker : null, lastLetter);
        if (level === 1) lastLetter = m.marker;
        paragraphs.push({ marker: m.marker, level, body });
    });

    // Render each paragraph; highlightText escapes its input, so plain
    // bodies are escaped explicitly before any markup is added
    return paragraphs
        .map(({ marker, level, body }) => {
            let safe = searchTerm ? highlightText(body, searchTerm) : escapeHtml(body);
            // In RTL, isolate the LTR islands (only when not mid-highlight, to
            // avoid wrapping <mark> markup). Designator is always LTR-isolated.
            if (rtl && !searchTerm) safe = isolateLtrRuns(safe);
            safe = safe.replace(
                /\[(\d+\s+FR\s+\d+[^\]]*)\]/g,
                '<em class="fr-citation">[$1]</em>'
            );
            const prefix = marker
                ? (rtl
                    ? `<strong><bdi dir="ltr">(${escapeHtml(marker)})</bdi></strong> `
                    : `<strong>(${escapeHtml(marker)})</strong> `)
                : '';
            return `<p class="reg-para level-${level}"${rtl ? ' dir="rtl"' : ''}>${prefix}${safe}</p>`;
        })
        .join('');
}

/**
 * SectionViewer Component
 * 
 * Displays regulation section content with proper formatting
 * Loads data from JSON, handles errors, shows metadata
 * 
 * Props:
 * - regulationCode: Regulation code (e.g., "1910", "1926")
 * - sectionCode: Section code (e.g., "22", "501")
 * - searchTerm: Optional search term to highlight
 * - onRelatedClick: Callback for related section clicks
 */
const SectionViewer = ({ 
    regulationCode,
    sectionCode,
    searchTerm = '',
    onRelatedClick = null
}) => {
    const [section, setSection] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Content language comes from the shared LanguageContext — the header's
    // nav switch and this in-section toggle drive the same state, so they
    // stay in sync and the choice persists across sections and reloads.
    // While the feature flag is off, section pages show a "coming soon"
    // notice instead of the toggle and the content stays in English.
    const translationsEnabled = FEATURES.CONTENT_TRANSLATIONS;
    const { lang, setLang, t } = useLanguage();
    const contentLanguage = translationsEnabled ? lang : 'en';
    const [translation, setTranslation] = useState(null);
    // 'idle' | 'loading' | 'ready' | 'unavailable'
    const [translationStatus, setTranslationStatus] = useState('idle');

    // Load section data
    useEffect(() => {
        const fetchSection = async () => {
            setLoading(true);
            setError(null);

            try {
                // Extract section number from full code (e.g., "1910.22" -> "22")
                const sectionNumber = sectionCode.includes('.')
                    ? sectionCode.split('.')[1]
                    : sectionCode;

                const { section: data, error: loadError } = await loadSection(
                    regulationCode,
                    sectionNumber
                );

                if (loadError) {
                    setError(loadError);
                } else {
                    setSection(data);
                }
            } catch (err) {
                setError('Failed to load section content');
                console.error('Section load error:', err);
            } finally {
                setLoading(false);
            }
        };

        if (regulationCode && sectionCode) {
            fetchSection();
        }
    }, [regulationCode, sectionCode]);

    // Load the translation when a non-English language is selected (or the
    // section changes). Works for any target language ('es', 'ar', …).
    useEffect(() => {
        if (!translationsEnabled || contentLanguage === 'en' || !regulationCode || !sectionCode) {
            setTranslation(null);
            setTranslationStatus('idle');
            return;
        }

        let cancelled = false;
        setTranslationStatus('loading');

        const sectionNumber = sectionCode.includes('.')
            ? sectionCode.split('.')[1]
            : sectionCode;

        loadSectionTranslation(regulationCode, sectionNumber, contentLanguage).then((result) => {
            if (cancelled) return;

            if (result.success) {
                setTranslation(result.translation);
                setTranslationStatus('ready');
            } else {
                setTranslation(null);
                setTranslationStatus('unavailable');
            }
        });

        return () => {
            cancelled = true;
        };
    }, [translationsEnabled, contentLanguage, regulationCode, sectionCode]);

    // Switch content language (shared context persists the choice)
    const handleLanguageChange = (nextLang) => {
        setLang(nextLang);
    };

    // Handle related section click
    const handleRelatedClick = (relatedCode, e) => {
        e.preventDefault();
        
        if (onRelatedClick && typeof onRelatedClick === 'function') {
            onRelatedClick(relatedCode);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="section-viewer loading">
                <div className="loading-spinner" />
                <p className="loading-text">{t('section.loading')}</p>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="section-viewer error">
                <div className="error-icon">{ICONS.WARNING || '⚠️'}</div>
                <h3 className="error-title">{t('section.loadErrorTitle')}</h3>
                <p className="error-message">{error}</p>
            </div>
        );
    }

    // No section found
    if (!section) {
        return (
            <div className="section-viewer empty">
                <p className="empty-message">{t('section.none')}</p>
            </div>
        );
    }

    // Calculate reading time
    const readingTime = section.wordCount
        ? t('section.minRead', { n: Math.max(1, calculateReadingTime(section.wordCount)) })
        : null;

    // Decode HTML entities
    const decodeEntities = (text) => {
        if (!text) return '';
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        return textarea.value;
    };

    // Whether a translation is the active content, and which language.
    const showingTranslation = contentLanguage !== 'en' && translationStatus === 'ready' && translation;
    const activeLang = showingTranslation ? contentLanguage : 'en';
    const isRTL = activeLang === 'ar';

    // Title/text of whichever language is being displayed
    const activeTitle = showingTranslation ? translation.title : section.title;
    const activeText = showingTranslation ? translation.text : section.text;
    const activeContent = showingTranslation ? null : section.content;

    // Titles arrive as "§ 1910.215 Abrasive wheel machinery." — strip the
    // leading section symbol + code so it isn't repeated next to section.code
    const getDisplayTitle = () => {
        return decodeEntities(activeTitle)
            .replace(/^\s*§?\s*\d{4}\.\d+[a-zA-Z]?\s*[-–—:]?\s*/, '')
            .trim();
    };

    // Render section content with proper formatting
    const renderContent = (content, text) => {
        // If we have structured content array, render it
        if (content && content.length > 0) {
            return content.map((item, index) => {
                // Highlight search terms if provided
                let displayText = item.text;
                if (searchTerm && searchTerm.length > 0) {
                    displayText = highlightText(item.text, searchTerm);
                }

                // Main sections (level 1)
                if (item.level === 1) {
                    return (
                        <section
                            key={item.id || index}
                            id={item.id}
                            className="content-section level-1"
                        >
                            <h3 className="section-heading">
                                {item.id && <span className="section-id">{item.id}</span>}
                                {item.title && <span className="section-title">{item.title}</span>}
                            </h3>
                            {displayText && (
                                <div
                                    className="section-text"
                                    dangerouslySetInnerHTML={{ __html: displayText }}
                                />
                            )}
                        </section>
                    );
                }

                // Subsections (level 2+)
                return (
                    <div
                        key={item.id || index}
                        id={item.id}
                        className={`content-subsection level-${item.level}`}
                    >
                        {item.title && (
                            <h4 className="subsection-heading">
                                {item.id && <span className="subsection-id">{item.id}</span>}
                                {item.title}
                            </h4>
                        )}
                        {displayText && (
                            <div
                                className="subsection-text"
                                dangerouslySetInnerHTML={{ __html: displayText }}
                            />
                        )}
                    </div>
                );
            });
        }

        // Fallback: render plain text content
        if (text) {
            let displayText = decodeEntities(text);

            // The text body often repeats the section title ("§ 1910.215
            // Abrasive wheel machinery. ...") — drop that duplicate prefix
            const decodedTitle = decodeEntities(activeTitle).trim();
            if (decodedTitle && displayText.startsWith(decodedTitle)) {
                displayText = displayText.slice(decodedTitle.length).trim();
            }

            const html = formatRegulationText(displayText, searchTerm, isRTL);

            return (
                <div
                    className="section-text formatted-text"
                    dir={isRTL ? 'rtl' : 'ltr'}
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            );
        }

        return <p>{t('section.noContent')}</p>;
    };

    return (
        <article className="section-viewer">
            {/* Section Header */}
            <header className="section-header">
                <h1 className="section-code-title" dir={isRTL ? 'rtl' : 'ltr'}>
                    <bdi dir="ltr">§ {section.code}</bdi> {getDisplayTitle()}
                </h1>

                {/* Metadata */}
                <div className="section-metadata">
                    {/* Code currency: when OSHA last revised this section, and
                        when we last pulled/synced the English from the eCFR. */}
                    {(section.amendmentDate || section.lastUpdated) && (
                        <span className="metadata-item updated">
                            {ICONS.CALENDAR || '📅'} {t('section.codeRevised', { d: formatDate(new Date(section.amendmentDate || section.lastUpdated), DATE_FORMATS.SHORT) })}
                        </span>
                    )}

                    {section.sourcePulledAt && (
                        <span className="metadata-item pulled">
                            🔄 {t('section.sourcePulled', { d: formatDate(new Date(section.sourcePulledAt), DATE_FORMATS.SHORT) })}
                        </span>
                    )}

                    {/* Translation currency: latest translation date — the active
                        translation's own date when one is displayed, otherwise the
                        section's recorded translation date. */}
                    {(() => {
                        const translationsDate =
                            (showingTranslation && translation.translatedDate) ||
                            section.translatedDate;
                        return translationsDate ? (
                            <span className="metadata-item translated">
                                🌐 {t('section.translationsUpdated', { d: formatDate(new Date(translationsDate), DATE_FORMATS.SHORT) })}
                            </span>
                        ) : (
                            <span className="metadata-item translated pending">
                                🌐 {t('section.translationsPending')}
                            </span>
                        );
                    })()}

                    {readingTime && (
                        <span className="metadata-item reading-time">
                            {ICONS.CLOCK || '🕐'} {readingTime}
                        </span>
                    )}

                    {section.critical && (
                        <span className="metadata-item badge critical">
                            {ICONS.WARNING || '⚠️'} {t('section.critical')}
                        </span>
                    )}

                    {section.popular && (
                        <span className="metadata-item badge popular">
                            {ICONS.STAR || '⭐'} {t('section.popular')}
                        </span>
                    )}
                </div>

                {/* Content Language Toggle — or a coming-soon notice while
                    the translation feature is held back */}
                {translationsEnabled ? (
                    <div className="language-toggle" role="group" aria-label="Content language">
                        {[
                            { code: 'en', label: 'English' },
                            { code: 'es', label: 'Español' },
                            { code: 'ar', label: 'العربية' },
                        ].map(({ code, label }) => (
                            <button
                                key={code}
                                type="button"
                                lang={code}
                                className={`language-toggle-btn ${contentLanguage === code ? 'active' : ''}`}
                                onClick={() => handleLanguageChange(code)}
                                aria-pressed={contentLanguage === code}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="translation-coming-soon" role="note">
                        <span aria-hidden="true">🌐</span>
                        <span>
                            Español · العربية — traducciones próximamente / translations coming soon
                        </span>
                    </div>
                )}
            </header>

            {/* HazCom sections pair naturally with actual Safety Data Sheets —
                point readers at the companion SDS.guide product. */}
            {HAZCOM_SECTIONS.includes(`${regulationCode}.${String(sectionCode).split('.').pop()}`) && (
                <div className="sds-guide-banner" role="note">
                    <span className="sds-guide-banner-icon" aria-hidden="true">🧪</span>
                    <div className="sds-guide-banner-text">
                        {activeLang === 'es' ? (
                            <>Esta norma exige hojas de datos de seguridad (SDS) accesibles. Busque y organice las SDS de su empresa gratis en{' '}</>
                        ) : activeLang === 'ar' ? (
                            <span dir="rtl" lang="ar">تتطلب هذه اللائحة إتاحة صحائف بيانات السلامة (SDS). ابحث عنها ونظّمها مجانًا في{' '}</span>
                        ) : (
                            <>This standard requires accessible Safety Data Sheets. Search and organize your company&apos;s SDS free at{' '}</>
                        )}
                        <a href="https://sds.guide" target="_blank" rel="noopener noreferrer">SDS.guide</a>
                    </div>
                </div>
            )}

            {/* Liability disclaimer — must stay visible (and print) whenever
                translated content is shown in place of the official text */}
            {showingTranslation && activeLang === 'es' && (
                <div className="translation-banner" role="note">
                    <span className="translation-banner-icon" aria-hidden="true">⚠️</span>
                    <div className="translation-banner-text">
                        <strong>Traducción automática no oficial.</strong> Esta traducción fue
                        generada por inteligencia artificial únicamente para su conveniencia.
                        El texto oficial y legalmente vinculante de esta regulación de OSHA es
                        la versión en inglés.
                        <span className="translation-banner-en">
                            Unofficial AI-generated translation. The official, legally binding
                            OSHA regulation is the English text.
                        </span>
                    </div>
                </div>
            )}
            {showingTranslation && activeLang === 'ar' && (
                <div className="translation-banner" role="note" dir="rtl" lang="ar">
                    <span className="translation-banner-icon" aria-hidden="true">⚠️</span>
                    <div className="translation-banner-text">
                        <strong>ترجمة آلية غير رسمية.</strong> أُنشئت هذه الترجمة
                        بواسطة الذكاء الاصطناعي لتسهيل الاطلاع فقط.
                        النص الرسمي والملزم قانونًا للائحة <bdi dir="ltr">OSHA</bdi> هو النص باللغة الإنجليزية.
                        <span className="translation-banner-en" dir="ltr">
                            Unofficial AI-generated translation. The official, legally binding
                            OSHA regulation is the English text.
                        </span>
                    </div>
                </div>
            )}

            {/* Legal-force key — clarifies mandatory vs. recommended vs. permitted
                wording so the reader does not misread a requirement. */}
            {showingTranslation && activeLang === 'es' && (
                <div className="legal-force-legend" role="note">
                    <span className="legal-force-icon" aria-hidden="true">⚖️</span>
                    <div className="legal-force-text">
                        <strong>Cómo leer los términos:</strong>{' '}
                        <span className="lf-term"><b>deberá</b> / <b>deberán</b> = se requiere (obligatorio)</span>
                        <span className="lf-sep"> · </span>
                        <span className="lf-term"><b>debería</b> = se recomienda (no obligatorio)</span>
                        <span className="lf-sep"> · </span>
                        <span className="lf-term"><b>podrá</b> / <b>podrán</b> = se permite (opcional)</span>
                    </div>
                </div>
            )}
            {showingTranslation && activeLang === 'ar' && (
                <div className="legal-force-legend" role="note" dir="rtl" lang="ar">
                    <span className="legal-force-icon" aria-hidden="true">⚖️</span>
                    <div className="legal-force-text">
                        <strong>كيفية قراءة المصطلحات:</strong>{' '}
                        <span className="lf-term"><b>يجب</b> = إلزامي (مطلوب)</span>
                        <span className="lf-sep"> · </span>
                        <span className="lf-term"><b>ينبغي</b> = يُوصى به (غير إلزامي)</span>
                        <span className="lf-sep"> · </span>
                        <span className="lf-term"><b>يجوز</b> = جائز (مسموح)</span>
                    </div>
                </div>
            )}

            {/* Translation requested but not yet available for this section */}
            {contentLanguage !== 'en' && translationStatus === 'unavailable' && (
                <div className="translation-unavailable" role="note">
                    <span aria-hidden="true">🌐</span>
                    <div>
                        {contentLanguage === 'ar' ? (
                            <>
                                <span dir="rtl" lang="ar"><strong>الترجمة العربية غير متوفرة لهذه المادة بعد.</strong> يُعرض النص الرسمي بالإنجليزية.</span>
                                <span className="translation-banner-en" dir="ltr">
                                    Arabic translation is not yet available for this section — showing the official English text.
                                </span>
                            </>
                        ) : (
                            <>
                                <strong>Traducción al español no disponible para esta sección todavía.</strong>{' '}
                                Se muestra el texto oficial en inglés.
                                <span className="translation-banner-en">
                                    Spanish translation is not yet available for this section — showing
                                    the official English text.
                                </span>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Section Content */}
            <div className="section-content" lang={activeLang} dir={isRTL ? 'rtl' : 'ltr'}>
                {contentLanguage !== 'en' && translationStatus === 'loading' ? (
                    <div className="translation-loading">
                        <div className="loading-spinner" />
                        <p>{contentLanguage === 'ar' ? 'جارٍ تحميل الترجمة…' : 'Cargando traducción…'}</p>
                    </div>
                ) : (
                    renderContent(activeContent, activeText)
                )}
            </div>

            {/* Related Sections */}
            {section.relatedSections && section.relatedSections.length > 0 && (
                <aside className="related-sections">
                    <h3 className="related-title">
                        {ICONS.LINK || '🔗'} {t('section.related')}
                    </h3>
                    <ul className="related-list">
                        {section.relatedSections.map((related) => (
                            <li key={related} className="related-item">
                                <a
                                    href={`#${related}`}
                                    className="related-link"
                                    onClick={(e) => handleRelatedClick(related, e)}
                                >
                                    {related}
                                </a>
                            </li>
                        ))}
                    </ul>
                </aside>
            )}
        </article>
    );
};

export default SectionViewer;
